import React, { useState, useEffect } from 'react';

const PaymentSuccessModal = ({ show, onClose, sessionId }) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && sessionId) {
      fetchPaymentDetails();
    }
  }, [show, sessionId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      // console.log('🔍 DEBUG Modal: fetchPaymentDetails appelé avec sessionId:', sessionId);

      // Utiliser uniquement la variable d'environnement (pas de hardcode)
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/souscriptions/paiement-session/${sessionId}/details`;
      // console.log('🔍 DEBUG Modal: URL API complète:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // console.log('🔍 DEBUG Modal: Response status:', response.status);
      // console.log('🔍 DEBUG Modal: Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        // console.log('❌ DEBUG Modal: Response error text:', errorText);
        throw new Error('Erreur lors de la récupération des détails');
      }

      const data = await response.json();
      // console.log('🔍 DEBUG Modal: Response data reçue:', data);

      if (data.success && data.souscription) {
        // console.log('✅ DEBUG Modal: Données valides, création paymentDetails');
        setPaymentDetails({
          reference: data.souscription.reference,
          clientName: data.souscription.client_name,
          serviceName: data.souscription.service_name,
          email: data.souscription.email,
          montant: data.souscription.montant_verse,
          statut: data.souscription.statut
        });
        // console.log('✅ DEBUG Modal: PaymentDetails défini avec succès');
      } else {
        // console.log('❌ DEBUG Modal: Données invalides - data.success:', data.success, 'data.souscription:', data.souscription);
        throw new Error(`Données invalides reçues - success: ${data.success}, souscription présente: ${!!data.souscription}`);
      }
    } catch (err) {
      console.error('Erreur fetchPaymentDetails:', err);
      setError('Erreur lors de la récupération des détails');
      // Fallback avec données par défaut
      setPaymentDetails({
        reference: 'SUB-XXXXXXX',
        clientName: 'Client',
        serviceName: 'Attestation de Logement et Prise en Charge',
        email: 'client@email.com'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl transform animate-slideIn overflow-hidden">

        {/* Header avec gradient success */}
        <div className="bg-gradient-to-r from-green-500 to-blue-500 px-6 py-6 text-center relative">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 transition-colors duration-200 bg-white bg-opacity-20 rounded-full p-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Icône de succès animée */}
          <div className="mx-auto mb-4 w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">
            🎉 Paiement Validé !
          </h2>
          <p className="text-green-100 text-sm">
            Processus de paiement exécuté avec succès
          </p>
        </div>

        {/* Contenu principal */}
        <div className="px-6 py-6 space-y-6">

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des détails...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="text-red-500 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <>
              {/* Informations de confirmation */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Détails de votre souscription
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Référence :</span>
                    <span className="font-mono font-medium text-blue-600">{paymentDetails?.reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service :</span>
                    <span className="font-medium text-right max-w-xs">{paymentDetails?.serviceName}</span>
                  </div>
                </div>
              </div>

              {/* Message principal */}
              <div className="text-center space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-green-800 mb-1">
                        ✅ Processus de paiement validé
                      </h4>
                      <p className="text-green-700 text-sm">
                        Votre paiement a été traité avec succès. Vous recevrez vos documents par email à <strong>{paymentDetails?.email}</strong> dans quelques instants après réception des fonds.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-blue-800 mb-1">
                        📋 Informations importantes
                      </h4>
                      <ul className="text-blue-700 text-sm space-y-2">
                        <li>
                          <strong>• Paiement par carte bancaire :</strong><br/>
                          <span className="text-blue-600">Traitement instantané. Vous recevrez vos documents par email dans quelques minutes.</span>
                        </li>
                        <li>
                          <strong>• Paiement par virement ou prélèvement SEPA :</strong><br/>
                          <span className="text-blue-600">Le traitement prendra 2 à 3 jours ouvrés. Vous recevrez vos documents par email après confirmation bancaire.</span>
                        </li>
                        <li className="pt-2 border-t border-blue-200">
                          <strong>Référence :</strong> <span className="font-mono">{paymentDetails?.reference}</span><br/>
                          <span className="text-blue-600">Conservez cette référence pour tout suivi</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section support */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>Besoin d'aide ?</span>
                  </div>
                  <span className="text-blue-600 font-medium">info@boaz-study.com</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer avec bouton */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-green-600 transition-all duration-300 font-medium flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0a2 2 0 012-2h6l2 2h6a2 2 0 012 2v1"></path>
            </svg>
            <span>Retour au Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;